import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { getUrlParams } from '../../../functions/get-url-params';
import { toInternalNavigationUrl } from '../../../functions/to-internal-navigation-url';
import { ListItem } from '../../../models/api/core/list/list-item';
import { FilterDetail } from '../../../models/api/reports/filter-detail-response';
import { ClientListService } from '../../../services/client-list/client-list.service';
import { FiltersService } from '../../../services/filters/filters.service';
import { ReportsService } from '../../../services/reports/reports.service';
import { Snackbar } from '../../../services/snackbar/snackbar.service';
import { BaseComponent } from '../../base/base.component';
import { BackButtonComponent } from '../../core/buttons/back-button.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { SelectFieldComponent } from '../../core/fields/select-field.component';
import { PageHeaderComponent } from '../../core/labels/page-header.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';
import { EmptyStateComponent } from '../../core/panels/empty-state.component';
import { HomeCardComponent } from '../../home/home-card.component';
import { EditFilterComponent } from './edit-filter.component';

@Component({
  selector: 'app-edit-filter-page',
  imports: [
    PageHeaderComponent,
    ActionRowComponent,
    ButtonComponent,
    EditFilterComponent,
    SelectFieldComponent,
    EmptyStateComponent,
    BackButtonComponent,
    HomeCardComponent,
  ],
  template: `
    <app-page-header
      [label]="
        isCopyMode ? 'Copy filter' : isEditMode ? 'Edit filter' : 'New filter'
      "
      headerSize="h1"
      paddingClasses="tw-pb-2"
    />
    <app-back-button (click)="confirmCancel()">Cancel</app-back-button>

    <div class="tw-grow tw-justify-center tw-w-full">
      <div class="tw-min-w-0 tw-w-[100%] sm:tw-pt-8 tw-mb-4">
        <app-home-card type="Large" overflow="visible">
          <div class="tw-min-w-0 tw-w-[100%] sm:tw-p-8">
            @if (isEditMode && !isCopyMode && reportsUsingFilter.length > 0) {
              <div
                class="tw-rounded-xl tw-border tw-bg-[var(--sed-open-grey-color)] tw-mt-6 tw-p-4 tw-mb-8 tw-flex tw-items-center tw-gap-4"
              >
                <div class="tw-w-[100px] tw-flex tw-justify-center">
                  <i
                    class="fa-duotone fa-light fa-message-exclamation tw-text-[54px]"
                    style="--fa-primary-color: #376ec8; --fa-secondary-color: #ffffff; --fa-secondary-opacity: 1"
                  ></i>
                </div>
                <div>
                  <div>
                    Editing this filter will affect the following reports:
                  </div>
                  <ul
                    class="tw-ml-4 tw-px-4 tw-py-1 tw-font-bold"
                    style="list-style-type: disc;"
                  >
                    @for (report of reportsUsingFilter; track report) {
                      <li class="tw-py-0.5">{{ report }}</li>
                    }
                  </ul>
                  <div class="tw-pt-2">
                    To avoid affecting these reports, cancel edit and select
                    Copy filter action.
                  </div>
                </div>
              </div>
            }

            <app-page-header label="Filter detail" headerSize="h2" />

            <app-select-field
              label="Client"
              [options]="client"
              [control]="fg.controls.client"
              valueClasses="tw-max-w-full sm:tw-max-w-[500px]"
              [disabled]="isEditMode && !(isCopyMode && isSystemFilterCopy)"
            />

            @if (fg.controls.client.value) {
              <app-edit-filter
                #editFilter
                [filterDetail]="filterDetail"
                [isCopyMode]="isCopyMode"
              />
              <app-action-row>
                <app-button
                  right1
                  label="Save"
                  [disabled]="
                    !fg.controls.client.value ||
                    !editFilter.fg.valid ||
                    editFilter.filtersEmpty() ||
                    !editFilter.hasAnyCriteria()
                  "
                  (click)="save()"
                ></app-button>
                <app-button
                  right2
                  label="Cancel"
                  emphasis="Low"
                  (click)="confirmCancel()"
                ></app-button>
              </app-action-row>
            } @else {
              <app-empty-state
                label="Select client"
                message="Select a client to continue"
                icon="fa-light fa-circle-info"
                height="120px"
              />
            }
          </div>
        </app-home-card>
      </div>
    </div>
  `,
  styles: ``,
})
export class EditFilterPageComponent extends BaseComponent implements OnInit {
  private route = inject(ActivatedRoute);

  @ViewChild(EditFilterComponent)
  editFilter: EditFilterComponent;

  private snackbar = inject(Snackbar);
  private reports = inject(ReportsService);
  private filters = inject(FiltersService);
  private clientListService = inject(ClientListService);

  filterId: string | null = null;
  saving = false;
  isEditMode = false;
  isCopyMode = false;
  isSystemFilterCopy = false;
  reportsUsingFilter: string[] = [];
  client: ListItem[];

  filterDetail: FilterDetail;
  previousClient: any = null;

  fg = new FormGroup({
    client: new FormControl<any>(null, [Validators.required]),
  });

  async ngOnInit() {
    this.dispose.subs = this.fg.controls.client.valueChanges.subscribe(
      (val) => {
        if (
          this.previousClient &&
          this.editFilter &&
          !this.editFilter.filtersEmpty()
        ) {
          this.confirm.show({
            label: 'Change client',
            message:
              'Are you sure you want to change the selected client?  All previously defined filter criteria will be removed.',
            width: '35rem',
            confirm: () => {
              this.previousClient = val;
              this.editFilter.resetAllSections();
              this.reports.selectedClient.set(val);
            },
            cancel: () => {
              this.fg.controls.client.setValue(this.previousClient, {
                emitEvent: false,
              });
            },
          });
        } else {
          this.previousClient = val;
          this.reports.selectedClient.set(val);
        }
      },
    );

    const filterIdParam = this.route.snapshot.paramMap.get('filterId');
    if (filterIdParam) {
      this.filterId = filterIdParam;
      this.isEditMode = true;
      this.isCopyMode =
        this.route.snapshot.queryParamMap.get('copy') === 'true';
      this.isSystemFilterCopy =
        this.route.snapshot.queryParamMap.get('isSystem') === 'true';
      await this.loadFilterData();
    }

    this.client = await this.clientListService.load();

    if (this.clientListService.isSingleClient()) {
      this.fg.controls.client.setValue(this.client[0].value);
      this.fg.controls.client.disable();
      this.reports.selectedClient.set(this.client[0].value);
    }
  }

  private async loadFilterData() {
    if (!this.filterId) return;

    const contractNumber =
      this.route.snapshot.queryParamMap.get('contractNumber') || null;
    const response = await this.api.filters.getFilterDetail(
      this.filterId,
      contractNumber,
    );

    this.filterDetail = { ...response.filterDetail };

    if (response.filterDetail.reportsUsingThisFilter) {
      this.reportsUsingFilter = response.filterDetail.reportsUsingThisFilter;
    }

    if (this.filterDetail?.contractNumber) {
      this.fg.controls.client.setValue(this.filterDetail.contractNumber);
    }
  }

  confirmCancel() {
    this.confirm.show({
      label: 'Cancel filter',
      message: `Are you sure you want leave? Any unsaved changes will be discarded.`,
      width: '35rem',
      confirm: () => this.handleRedirect(),
    });
  }

  handleRedirect() {
    const params = getUrlParams();
    const internalUrl = params.redirectTo
      ? toInternalNavigationUrl(params.redirectTo)
      : null;

    if (internalUrl) {
      const redirect = new URL(internalUrl, window.location.origin);
      const contractNumber = this.fg.controls.client.value;
      if (contractNumber) {
        redirect.searchParams.set('contractNumber', String(contractNumber));
      }
      this.router.navigateByUrl(
        `${redirect.pathname}${redirect.search}${redirect.hash}`,
      );
    } else {
      this.router.navigate(['/reports'], { queryParams: { tab: 'filters' } });
    }
  }

  async save() {
    if (this.saving) return;
    if (!this.editFilter?.fg.controls.filterName.valid) {
      this.editFilter?.fg.controls.filterName.markAllAsTouched();
      this.snackbar.show(
        'Please enter a filter name',
        'Error',
        'fa-light fa-exclamation-triangle',
        3000,
      );
      return;
    }

    if (!this.editFilter?.validCriteria()) {
      return;
    }

    const allCriteria = this.editFilter?.getCriteria() || [];
    const filterName = this.editFilter?.fg.controls.filterName.value;
    const contractNumber = this.fg.controls.client.value || '';

    this.saving = true;
    const savedFilterId = await this.filters.saveFilter(
      contractNumber,
      filterName,
      allCriteria,
      this.isCopyMode ? null : this.filterId,
    );

    if (savedFilterId) {
      this.snackbar.show(
        this.isEditMode && !this.isCopyMode ? 'Edits saved' : 'Filter saved',
        'Success',
        'fa-light fa-check',
        3000,
      );
      this.filters.invalidateFilterDetail(this.filterId);
      this.filters.needsRefresh.set(true);
      this.handleRedirect();
    }
    this.saving = false;
  }
}
